
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Tabs,
  Button,
  Descriptions,
  Badge,
  Divider,
  Typography,
  Space,
  List,
  Avatar,
  Tag,
  Dropdown,
  Modal,
  message,
  Col,
  Row,
  Spin,
  Alert,
} from 'antd';
import {
  DownOutlined,
  EllipsisOutlined,
  UserDeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import Menu, { MenuProps } from 'antd/es/menu/menu';
import {
  Estate,
  ExecutorInviteResponse,
  Executor,
  ParticipantResponse,
  User,
} from '../../types';
import Cookies from 'js-cookie';
import moment from 'moment';
import AddEstateModal from './addestate';
import AddExecutorModal from '../executor/addexecutor';
import AddParticipantModal from '../participant/addparticipant';
import { deleteParticipant } from '../participant/participantService';
import { addExecutorToParticipants, deleteEstate, fetchEstateDetails } from './estateService';
import AuditTrail from '../audittrail/audittrail';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

// Styled Components
const EstateContainer = styled.div`
  max-width: 800px;
  margin: auto;
  padding: 1rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const ParticipantItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 8px;
  background-color: #f4f6fb;
  margin-bottom: 0.5rem;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  &:hover {
    border-color: #3b82f6;
  }
`;

const ParticipantDetails = styled.div`
  flex-grow: 1;
  margin-left: 1rem;
`;

const ParticipantActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ParticipantHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EstateDetails: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const [role, setRole] = useState<string>('Admin');
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isAddParticipantModalVisible, setIsAddParticipantModalVisible] = useState(false);
  const [isAddExecutorModalVisible, setIsAddExecutorModalVisible] = useState(false);

  const [participants, setParticipants] = useState<ParticipantResponse[]>([]);
  const [executors, setExecutors] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [invites, setInvites] = useState<User[]>([]);

  const [invitedGuests, setInvitedGuests] = useState<any[]>([]);

  const [estateDetails, setEstateDetails] = useState<Estate | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // For editing a participant
  const [participantToEdit, setParticipantToEdit] = useState<ParticipantResponse | null>(null);

  // Role Menu items and handler
  const items: MenuProps['items'] = [
    { label: 'User', key: 'User' },
    { label: 'Guest', key: 'Guest' },
    { label: 'Admin', key: 'Admin', danger: true, disabled: true },
    { label: 'Executor', key: 'Executor', danger: true },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    message.info(`Role changed to ${e.key}`);
    handleRoleChange(e.key);
  };

  const handleRoleChange = (newRole: string) => {
    Modal.confirm({
      title: 'Confirm Role Change',
      content: `Are you sure you want to change the role to ${newRole}?`,
      onOk: () => setRole(newRole),
      okText: 'Yes',
      cancelText: 'No',
    });
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const RoleMenu = (
    <Dropdown menu={menuProps}>
      <Button>
        <Space>
          Change Role
          <DownOutlined />
        </Space>
      </Button>
    </Dropdown>
  );

  // Menu items for estate-level actions (currently only delete)
  const estateActionsMenuItems: MenuProps['items'] = [
    {
      label: 'Delete Estate',
      key: 'delete',
      danger: true,
    },
  ];

  // Handle click on the estate actions menu
  const handleEstateActionClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'delete') {
      handleDeleteEstate();
    }
  };

  const estateActionsMenuProps = {
    items: estateActionsMenuItems,
    onClick: handleEstateActionClick,
  };

  // Function to delete the estate
  const handleDeleteEstate = () => {
    Modal.confirm({
      title: 'Confirm Estate Deletion',
      content: 'Are you sure you want to delete this estate?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const token = Cookies.get('token') || Cookies.get('accessToken');
          if (!token) {
            message.error('Unauthorized. Please log in.');
            return;
          }

          await deleteEstate(estateId!, token);
          message.success('Estate deleted successfully');
          navigate('/estates'); // Navigate back to the estates list page (adjust as needed)
        } catch (error: any) {
          console.error(error);
          message.error(error.message || 'Failed to delete estate');
        }
      },
    });
  };

  const handleAddExecutorToParticipant = async (executorUid: string) => {
    try {
      const token = Cookies.get('token') || Cookies.get('accessToken');
      if (!token || !estateId) {
        message.error('Unauthorized. Please log in.');
        return;
      }
  
      await addExecutorToParticipants(estateId, executorUid);
      
      // Refresh the estate details to show the new participant
      const updatedEstate = await fetchEstateDetails(estateId, token);
      setEstateDetails(updatedEstate);
      setParticipants(updatedEstate.participants as ParticipantResponse[]);
      
      message.success('Executor added to participants successfully');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || 'Failed to add executor to participants');
    }
  };

  // Fetch Estate Details on Mount (for viewing/editing existing estate)
  useEffect(() => {
    const loadEstateDetails = async () => {
      try {
        const token = Cookies.get('token') || Cookies.get('accessToken'); // Adjust the cookie name as needed
        if (!token) {
          message.error('Unauthorized. Please log in.');
          navigate('/login'); // Redirect to login or appropriate page
          return;
        }

        const estateData: Estate = await fetchEstateDetails(estateId!, token);

        setEstateDetails({
          ...estateData,
        });

        // Participants
        setParticipants(estateData.participants as ParticipantResponse[]);

        // Executors
        setExecutors(estateData.executor);

        // Guests
        setInvitedGuests(estateData.guests);

        // Invites
        setInvites(estateData.invites);

        // Users
        setUsers(estateData.users);

        setIsLoading(false);
      } catch (err: any) {
        console.error(err);
        if (err.message === 'Unauthorized') {
          message.error('Unauthorized. Please log in.');
          navigate('/login');
        } else {
          setError(err.message || 'Failed to load estate details.');
        }
        setIsLoading(false);
      }
    };

    // Only fetch if estateId is provided (i.e., viewing/editing an existing estate)
    if (estateId) {
      loadEstateDetails();
    } else {
      setIsLoading(false); // No estate to load
    }
  }, [estateId, navigate]);

  // Handle Success of Estate Edit or Create
  const handleEstateSubmit = (estate: Estate) => {
    if (estateId) {
      // Editing existing estate
      setEstateDetails(estate);
      message.success('Estate details updated successfully');
    } else {
      // Creating new estate
      setEstateDetails(estate);
      message.success('Estate created successfully');
      // Optionally, navigate to the new estate's details page
      navigate(`/estates/${estate.uuid}`);
    }
  };

  // Handle Success of Adding or Editing a Participant
  const handleParticipantSuccess = (participant: ParticipantResponse) => {
    if (participantToEdit) {
      // Editing existing participant
      setParticipants((prev) =>
        prev.map((p) => (p.id === participant.id ? participant : p))
      );
      setParticipantToEdit(null);
    } else {
      // Adding new participant

      setParticipants((prev) => [...prev, participant]);
    }
    message.success(`Participant ${participantToEdit ? 'updated' : 'added'} successfully`);
  };

  // Handle Success of Executor Invitation
  const handleExecutorInviteSuccess = (response: ExecutorInviteResponse) => {
    // Optionally, refresh the executors list or append the new executor if API returns executor data
    message.success(response.message);
    // If the executor appears in the list immediately, you can append it as follows:
    // setExecutors([...executors, newExecutor]);
  };

  // Handle Deleting a Participant
  const handleDeleteParticipant = (participantId: string) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this participant?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const token = Cookies.get('token') || Cookies.get('accessToken');
          if (!token) {
            message.error('Unauthorized. Please log in.');
            return;
          }

          await deleteParticipant(estateId!, participantId, token);
          setParticipants((prev) => prev.filter((p) => p.uid !== participantId));
          message.success('Participant deleted successfully');
        } catch (error: any) {
          console.error(error);
          message.error(error.message || 'Failed to delete participant');
        }
      },
    });
  };

  // Handle Editing a Participant
  const handleEditParticipant = (participant: ParticipantResponse) => {
    setParticipantToEdit(participant);
    setIsAddParticipantModalVisible(true);
  };

  if (isLoading) {
    return (
      <EstateContainer>
        <Spin tip="Loading estate details..." />
      </EstateContainer>
    );
  }

  if (error) {
    return (
      <EstateContainer>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        />
      </EstateContainer>
    );
  }

  return (
    <Row gutter={16} style={{ marginTop: '24px' }}>
      {/* Estate Details and Actions */}
      <Col span={16}>
        <EstateContainer>
          {/* Custom Header */}
          <HeaderContainer>
            <Space direction="vertical">
              <Title level={4}>Estate {estateId ? `#${estateId.slice(0, -12)}` : ''}</Title>
              <Space>
                <Badge status="processing" text="In progress" />
                <Text type="secondary">In progress</Text>
              </Space>
            </Space>
            <Space>
              {estateId && (
                <Button
                  type="primary"
                  onClick={() => setIsEditModalVisible(true)}
                >
                  Edit
                </Button>
              )}
              {estateId ? (
                <>
                  <Button onClick={() => setIsAddParticipantModalVisible(true)}>
                    Add Participant
                  </Button>
                  <Button onClick={() => setIsAddExecutorModalVisible(true)}>
                    Add Executor
                  </Button>
                </>
              ) : (
                <Button onClick={() => navigate('/create-estate')}>
                  Create Estate
                </Button>
              )}
              <Dropdown menu={estateActionsMenuProps}>
                <Button icon={<EllipsisOutlined />} />
              </Dropdown>
            </Space>
          </HeaderContainer>

          {estateId && (
            <>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Estate Name">
                  {estateDetails?.estateName}
                </Descriptions.Item>
                <Descriptions.Item label="Owner">
                  {`${estateDetails?.estateOwner.prefix || ''} ${estateDetails?.estateOwner.firstName || ''} ${estateDetails?.estateOwner.middleName || ''} ${estateDetails?.estateOwner.lastName || ''} ${estateDetails?.estateOwner.suffix || ''}`}
                </Descriptions.Item>
                <Descriptions.Item label="Start Date">
                  {estateDetails?.createdAt
                    ? new Date(estateDetails.createdAt).toLocaleString()
                    : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Address">
                  {estateDetails?.address || 'N/A'}
                </Descriptions.Item>
              </Descriptions>
              <Divider />

              <Tabs defaultActiveKey="1">
                {/* Participants Tab */}
                <TabPane tab="Participants" key="1">
                  <List
                    dataSource={participants}
                    renderItem={(participant) => (
                      <ParticipantItem key={participant.id}>
                        <Avatar src={participant.profilePic ? `https://i.pravatar.cc/40?u=${participant.id}` : `https://i.pravatar.cc/40?img=${participant.id}`} />
                        <ParticipantDetails>
                          <ParticipantHeader>
                            <Text strong>{`${participant.prefix || ''} ${participant.firstName} ${participant.middleName || ''} ${participant.lastName || ''}`.trim()}</Text>
                          </ParticipantHeader>
                          <Text type="secondary">{participant.email}, </Text>
                          <Tag color="blue">{participant.addresses?.[0] && participant.addresses?.[0].city}</Tag>
                        </ParticipantDetails>
                        <ParticipantActions>
                          {RoleMenu}
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditParticipant(participant)}
                          />
                          <Button
                            type="text"
                            icon={<UserDeleteOutlined />}
                            onClick={() => handleDeleteParticipant(participant.uid)}
                          />
                        </ParticipantActions>
                      </ParticipantItem>
                    )}
                  />
                </TabPane>

                {/* Executors Tab */}
                <TabPane tab="Executors" key="2">
                  <List
                    dataSource={executors}
                    renderItem={(executor) => (
                      <ParticipantItem key={executor.id}>
                        <Avatar src={executor.profilePic ? executor.profilePic : `https://i.pravatar.cc/40?img=${executor.id}`} />
                        <ParticipantDetails>
                          <ParticipantHeader>
                            <Text strong>{`${executor.prefix || ''} ${executor.firstName} ${executor.middleName || ''} ${executor.lastName || ''}`.trim()}</Text>
                          </ParticipantHeader>
                          <Text type="secondary">{executor.email}</Text>
                          <Tag color="green">{executor.isAdmin ? 'Admin' : 'Executor'}</Tag>
                        </ParticipantDetails>
                        <ParticipantActions>
                        <Button 
            type="primary" 
            onClick={() => handleAddExecutorToParticipant(executor.uid)}
          >
            Add as Participant
          </Button>
                          {RoleMenu}
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditParticipant(executor as ParticipantResponse)}
                          />
                          <Button
                            type="text"
                            icon={<UserDeleteOutlined />}
                            onClick={() => handleDeleteParticipant(executor.uid)}
                          />
                        </ParticipantActions>
                      </ParticipantItem>
                    )}
                  />
                </TabPane>

                {/* Invited Guests Tab */}
                <TabPane tab="Invites" key="3">
                  <List
                    dataSource={invites}
                    renderItem={(invite) => (
                      <ParticipantItem key={invite.id}>
                        <Avatar src={invite.profilePic ? invite.profilePic : `https://i.pravatar.cc/40?u=${invite.id}`} />
                        <ParticipantDetails>
                          <ParticipantHeader>
                            <Text strong>{`${invite.prefix || ''} ${invite.firstName} ${invite.middleName || ''} ${invite.lastName || ''}`.trim()}</Text>
                          </ParticipantHeader>
                          <Text type="secondary">{invite.email}</Text>
                          <Tag color="purple">Invites</Tag>
                        </ParticipantDetails>
                        <ParticipantActions>
                          {RoleMenu}
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditParticipant(invite as ParticipantResponse)}
                          />
                          <Button
                            type="text"
                            icon={<UserDeleteOutlined />}
                            onClick={() => handleEditParticipant(invite as ParticipantResponse)}
                          />
                        </ParticipantActions>
                      </ParticipantItem>
                    )}
                  />
                </TabPane>

                {/* Invited Guests Tab */}
                <TabPane tab="Guests" key="4">
                  <List
                    dataSource={invitedGuests}
                    renderItem={(guest) => (
                      <ParticipantItem key={guest.id}>
                        <Avatar src={guest.profilePic ? guest.profilePic : `https://i.pravatar.cc/40?u=${guest.id}`} />
                        <ParticipantDetails>
                          <ParticipantHeader>
                            <Text strong>{`${guest.prefix || ''} ${guest.firstName} ${guest.middleName || ''} ${guest.lastName || ''}`.trim()}</Text>
                          </ParticipantHeader>
                          <Text type="secondary">{guest.email}</Text>
                          <Tag color="purple">Guest</Tag>
                        </ParticipantDetails>
                        <ParticipantActions>
                          {RoleMenu}
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditParticipant(guest as ParticipantResponse)}
                          />
                          <Button
                            type="text"
                            icon={<UserDeleteOutlined />}
                            onClick={() => handleDeleteParticipant(guest.uid)}
                          />
                        </ParticipantActions>
                      </ParticipantItem>
                    )}
                  />
                </TabPane>
              </Tabs>
            </>
          )}

          {/* Edit Estate Modal */}
          {estateId && (
            <AddEstateModal
              open={isEditModalVisible}
              onCancel={() => setIsEditModalVisible(false)}
              onSubmit={handleEstateSubmit}
              initialValues={
                estateDetails
                  ? {
                    uuid: estateDetails.uuid,
                    estateName: estateDetails.estateName,
                    description: estateDetails.description,
                    prefix: estateDetails.estateOwner.prefix || '',
                    firstName: estateDetails.estateOwner.firstName || '',
                    middleName: estateDetails.estateOwner.middleName || '',
                    lastName: estateDetails.estateOwner.lastName || '',
                    suffix: estateDetails.estateOwner.suffix || '',
                    dob: estateDetails.estateOwner.dob ? moment(estateDetails.estateOwner.dob) : null,
                    sex: estateDetails.estateOwner.sex || '',
                    isDeceased: estateDetails.estateOwner.deceased || false,
                  }
                  : undefined
              }
              isEdit={true}
            />
          )}

          {/* Add or Edit Participant Modal */}
          <AddParticipantModal
            visible={isAddParticipantModalVisible}
            onCancel={() => {
              setIsAddParticipantModalVisible(false);
              setParticipantToEdit(null);
            }}
            onSuccess={handleParticipantSuccess}
            estateId={estateId!}
            estate={estateDetails}
            initialValues={participantToEdit || undefined}
            isEdit={participantToEdit ? true : false}
          />

          {/* Add Executor Modal */}
          {estateId && (
            <AddExecutorModal
              open={isAddExecutorModalVisible}
              onCancel={() => setIsAddExecutorModalVisible(false)}
              onSuccess={handleExecutorInviteSuccess}
              estateId={estateId!}
            />
          )}
        </EstateContainer>
      </Col>
      <Col span={8}>
        <AuditTrail />
      </Col>
    </Row>
  );
};

export default EstateDetails;
